import React, { useEffect, useState } from 'react';
import api from '@/utils/api';
import { Building2, Plus, Users, Package, Calendar, Check, X, Globe, Shield, Eye, Edit2, Trash2, AlertTriangle, CreditCard, Wallet, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  
  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    admin_email: '',
    admin_password: '',
    admin_name: ''
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/tenants');
      setTenants(response.data);
      setIsSuperadmin(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsSuperadmin(false);
        toast.error('Superadmin access required to manage tenants');
      } else {
        toast.error('Failed to load tenants');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    // Validate form
    if (!formData.slug || !formData.name || !formData.admin_email || !formData.admin_password) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate slug format
    const slugRegex = /^[a-z][a-z0-9-]{2,49}$/;
    if (!slugRegex.test(formData.slug.toLowerCase())) {
      toast.error('Slug must be 3-50 chars, start with a letter, and contain only lowercase letters, numbers, and hyphens');
      return;
    }

    if (formData.admin_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setCreating(true);
      const response = await api.post('/admin/tenants', {
        ...formData,
        slug: formData.slug.toLowerCase()
      });
      
      toast.success(`Tenant "${response.data.tenant.slug}" created successfully!`);
      setCreateDialogOpen(false);
      setFormData({
        slug: '',
        name: '',
        admin_email: '',
        admin_password: '',
        admin_name: ''
      });
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const handleViewDetails = async (tenant) => {
    try {
      const response = await api.get(`/admin/tenants/${tenant.tenant_id}`);
      setSelectedTenant(response.data);
      setDetailsDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load tenant details');
    }
  };

  const handleUpdateStatus = async (tenantId, newStatus) => {
    try {
      await api.put(`/admin/tenants/${tenantId}`, { status: newStatus });
      toast.success('Tenant status updated');
      loadTenants();
    } catch (error) {
      toast.error('Failed to update tenant status');
    }
  };

  const handleUpdateSubscription = async (tenantId, subscriptionType) => {
    try {
      await api.put(`/admin/tenants/${tenantId}/subscription`, { subscription_type: subscriptionType });
      toast.success(`Subscription updated to ${subscriptionType === 'fulfillment' ? 'Fulfillment by Golfshop.fit' : 'Self-Managed'}`);
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleToggleFreeAccess = async (tenantId, enabled) => {
    try {
      const res = await api.put(`/admin/tenants/${tenantId}/free-access`, { enabled });
      const canceledCount = res.data?.canceled_stripe_subscriptions?.length || 0;
      if (canceledCount > 0) {
        toast.success(`Free access granted — ${canceledCount} active Stripe subscription${canceledCount > 1 ? 's' : ''} canceled so they won't be billed again`);
      } else {
        toast.success(enabled ? 'Free access granted' : 'Free access revoked');
      }
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update free access');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
  };

  const handleDeleteClick = (tenant) => {
    setTenantToDelete(tenant);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteTenant = async () => {
    if (!tenantToDelete) return;
    
    // Safety check - must type tenant slug to confirm
    if (deleteConfirmText !== tenantToDelete.slug) {
      toast.error('Please type the store URL to confirm deletion');
      return;
    }
    
    try {
      setDeleting(true);
      const response = await api.delete(`/admin/tenants/${tenantToDelete.tenant_id}`);
      toast.success(response.data.message || 'Tenant deleted successfully');
      setDeleteDialogOpen(false);
      setTenantToDelete(null);
      setDeleteConfirmText('');
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12" data-testid="tenants-loading">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading tenants...</p>
      </div>
    );
  }

  if (!isSuperadmin) {
    return (
      <div className="text-center py-12" data-testid="tenants-access-denied">
        <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Superadmin Access Required</h2>
        <p className="text-gray-600">
          Only administrators of the default tenant can manage tenants.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="admin-tenants-page">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Tenant Management
        </h2>
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="bg-[#4A6741] hover:bg-[#3d5636]"
          data-testid="create-tenant-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Tenant
        </Button>
      </div>

      {/* Tenants Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tenants-grid">
        {tenants.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg border border-[#F3F4F6]">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No tenants found. Create your first tenant to get started.</p>
          </div>
        ) : (
          tenants.map((tenant) => (
            <div 
              key={tenant.tenant_id} 
              className="bg-white rounded-lg border border-[#E8E4DC] overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`tenant-card-${tenant.slug}`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{tenant.name}</h3>
                    <p className="text-sm text-gray-500 flex items-center mt-1">
                      <Globe className="h-3 w-3 mr-1" />
                      /{tenant.slug}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                      tenant.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {tenant.status === 'active' ? <Check className="h-3 w-3 mr-1" /> : <X className="h-3 w-3 mr-1" />}
                      {tenant.status}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                      tenant.subscription_type === 'fulfillment'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-50 text-gray-500'
                    }`} data-testid={`sub-badge-${tenant.slug}`}>
                      {tenant.subscription_type === 'fulfillment' ? (
                        <><Wallet className="h-3 w-3 mr-1" />Fulfillment</>
                      ) : (
                        <>Self-Managed</>
                      )}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center text-gray-500 text-xs mb-1">
                      <Users className="h-3 w-3 mr-1" />
                      Users
                    </div>
                    <p className="text-lg font-bold text-gray-900">{tenant.user_count || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center text-gray-500 text-xs mb-1">
                      <Package className="h-3 w-3 mr-1" />
                      Products
                    </div>
                    <p className="text-lg font-bold text-gray-900">{tenant.product_count || 0}</p>
                  </div>
                </div>

                <div className="text-xs text-gray-400 mb-4">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  Created {new Date(tenant.created_at).toLocaleDateString()}
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(tenant)}
                      className="flex-1"
                      data-testid={`view-tenant-${tenant.slug}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Details
                    </Button>
                    <Select
                      value={tenant.status}
                      onValueChange={(value) => handleUpdateStatus(tenant.tenant_id, value)}
                    >
                      <SelectTrigger className="w-[100px]" data-testid={`status-select-${tenant.slug}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(tenant)}
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                      data-testid={`delete-tenant-${tenant.slug}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Select
                    value={tenant.subscription_type || 'self_managed'}
                    onValueChange={(value) => handleUpdateSubscription(tenant.tenant_id, value)}
                  >
                    <SelectTrigger className="w-full text-xs" data-testid={`subscription-select-${tenant.slug}`}>
                      <Wallet className="h-3 w-3 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="self_managed">Self-Managed</SelectItem>
                      <SelectItem value="fulfillment">Fulfillment by Golfshop.fit</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center justify-between px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`free-access-row-${tenant.slug}`}>
                    <div className="flex items-center text-xs font-medium text-amber-800">
                      <Gift className="h-3.5 w-3.5 mr-1.5" />
                      Free Access
                    </div>
                    <Switch
                      checked={!!tenant.free_access}
                      onCheckedChange={(checked) => handleToggleFreeAccess(tenant.tenant_id, checked)}
                      data-testid={`free-access-toggle-${tenant.slug}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Tenant Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Store Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Golfshop.fit"
                value={formData.name}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    name: e.target.value,
                    slug: prev.slug || generateSlug(e.target.value)
                  }));
                }}
                className="bg-gray-50 border-[#F3F4F6]"
                data-testid="tenant-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Store URL *</Label>
              <div className="flex items-center">
                <span className="text-gray-500 text-sm mr-2">/</span>
                <Input
                  id="slug"
                  placeholder="e.g., bobs-golf"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase() }))}
                  className="bg-gray-50 border-[#F3F4F6]"
                  data-testid="tenant-slug-input"
                />
              </div>
              <p className="text-xs text-gray-500">
                This will be the store's web address (e.g., /bobs-golf)
              </p>
            </div>

            <div className="border-t border-[#F3F4F6] pt-4 mt-4">
              <h4 className="text-sm font-bold text-gray-700 mb-3">Initial Admin User</h4>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="admin_email">Admin Email *</Label>
                  <Input
                    id="admin_email"
                    type="email"
                    placeholder="admin@example.com"
                    value={formData.admin_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, admin_email: e.target.value }))}
                    className="bg-gray-50 border-[#F3F4F6]"
                    data-testid="admin-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin_password">Admin Password *</Label>
                  <Input
                    id="admin_password"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={formData.admin_password}
                    onChange={(e) => setFormData(prev => ({ ...prev, admin_password: e.target.value }))}
                    className="bg-gray-50 border-[#F3F4F6]"
                    data-testid="admin-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin_name">Admin Name (optional)</Label>
                  <Input
                    id="admin_name"
                    placeholder="e.g., John Smith"
                    value={formData.admin_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, admin_name: e.target.value }))}
                    className="bg-gray-50 border-[#F3F4F6]"
                    data-testid="admin-name-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateTenant}
              disabled={creating}
              className="bg-[#4A6741] hover:bg-[#3d5636]"
              data-testid="submit-create-tenant"
            >
              {creating ? 'Creating...' : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-700">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Tenant
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              This action is permanent and cannot be undone. All data associated with this tenant will be deleted, including products, orders, users, and settings.
            </DialogDescription>
          </DialogHeader>
          
          {tenantToDelete && (
            <div className="space-y-4 py-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-bold text-red-800 mb-1">{tenantToDelete.name}</p>
                <p className="text-xs text-red-600">/{tenantToDelete.slug}</p>
                <div className="flex gap-4 mt-2 text-xs text-red-600">
                  <span>{tenantToDelete.user_count || 0} users</span>
                  <span>{tenantToDelete.product_count || 0} products</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm text-gray-700">
                  Type <span className="font-mono font-bold text-red-700">{tenantToDelete.slug}</span> to confirm deletion:
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={tenantToDelete.slug}
                  className="border-red-200 focus:border-red-400"
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setDeleteDialogOpen(false); setTenantToDelete(null); setDeleteConfirmText(''); }}
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTenant}
              disabled={deleting || deleteConfirmText !== tenantToDelete?.slug}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="delete-confirm-btn"
            >
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Tenant Details</DialogTitle>
          </DialogHeader>
          
          {selectedTenant && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Tenant ID</p>
                  <p className="font-mono text-sm text-gray-900 truncate">{selectedTenant.tenant_id}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Status</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                    selectedTenant.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {selectedTenant.status}
                  </span>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Name</p>
                  <p className="font-medium text-gray-900">{selectedTenant.name}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">URL</p>
                  <p className="text-gray-900">/{selectedTenant.slug}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <Users className="h-5 w-5 text-[#4A6741] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{selectedTenant.user_count || 0}</p>
                  <p className="text-xs text-gray-500">Users</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <Package className="h-5 w-5 text-[#4A6741] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{selectedTenant.product_count || 0}</p>
                  <p className="text-xs text-gray-500">Products</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <Building2 className="h-5 w-5 text-[#4A6741] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{selectedTenant.order_count || 0}</p>
                  <p className="text-xs text-gray-500">Orders</p>
                </div>
              </div>

              {selectedTenant.admin_users && selectedTenant.admin_users.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-2">Admin Users</h4>
                  <div className="space-y-2">
                    {selectedTenant.admin_users.map((admin) => (
                      <div key={admin.user_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{admin.name}</p>
                          <p className="text-xs text-gray-500">{admin.email}</p>
                        </div>
                        <span className="text-xs bg-[#D4A55A]/20 text-[#D4A55A] px-2 py-1 rounded font-bold">
                          Admin
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-400 border-t pt-4">
                Created: {new Date(selectedTenant.created_at).toLocaleString()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
